import { Link } from 'react-router-dom';
import type { ProductCardDto } from '../lib/api';
import { discountPercent, price } from '../lib/format';

/**
 * Product image with a blurred placeholder underneath.
 *
 * The migration stores a ~20px blurred webp inline on each product, so the card
 * shows the shape and colour of the piece immediately and the real photo fades
 * in over it. On a jewellery shop the imagery *is* the product, so avoiding a
 * grid of empty grey boxes matters.
 */
export function ProductImage({
  image,
  sizes,
  className = '',
  eager = false,
}: {
  image: ProductCardDto['images'][number] | undefined;
  sizes: string;
  className?: string;
  eager?: boolean;
}) {
  if (!image) {
    return (
      <div
        className={`flex items-center justify-center bg-surface text-xs text-muted ${className}`}
      >
        No photograph yet
      </div>
    );
  }

  return (
    <img
      src={image.src}
      srcSet={image.srcset}
      sizes={sizes}
      alt={image.alt}
      width={image.width}
      height={image.height}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      // fetchPriority high on the first image improves LCP measurably.
      fetchPriority={eager ? 'high' : 'auto'}
      className={className}
      style={
        image.placeholder
          ? {
              backgroundImage: `url(${image.placeholder})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : undefined
      }
    />
  );
}

export function SaleBadge({ percent }: { percent: number }) {
  if (percent <= 0) return null;
  return (
    <span className="absolute left-3 top-3 bg-sale px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
      {percent}% off
    </span>
  );
}

export function ProductCard({
  product,
  eager = false,
}: {
  product: ProductCardDto;
  eager?: boolean;
}) {
  const [primary, secondary] = product.images;
  const percent = product.compareAtMinor
    ? discountPercent(product.priceMinor, product.compareAtMinor)
    : 0;

  return (
    <article className="group">
      <Link to={`/products/${product.slug}`} className="block">
        <div className="relative aspect-square w-full overflow-hidden bg-surface">
          <ProductImage
            image={primary}
            eager={eager}
            sizes="(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, 50vw"
            className="h-full w-full object-cover transition-opacity duration-500 group-hover:opacity-0"
          />
          {/* Second photograph revealed on hover — most pieces are shot from
              two angles, and it saves a click to compare. */}
          {secondary && (
            <ProductImage
              image={secondary}
              sizes="(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, 50vw"
              className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            />
          )}

          <SaleBadge percent={percent} />

          {!product.inStock && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/70">
              <span className="border border-ink px-3 py-1.5 text-[10px] uppercase tracking-widest">
                Sold
              </span>
            </div>
          )}
        </div>

        <div className="mt-4 space-y-1.5">
          {/* Titles imported from Shopify are long and keyword-stuffed; the
              part before the first pipe is the actual product name. */}
          <h3 className="text-sm leading-snug text-ink">
            {product.title.split('|')[0]?.trim() ?? product.title}
          </h3>
          <p className="flex items-baseline gap-2 text-sm">
            <span className={product.compareAtMinor ? 'text-sale' : 'text-ink'}>
              {price(product.priceMinor, product.currency as 'GBP')}
            </span>
            {product.compareAtMinor && (
              <span className="text-xs text-muted line-through">
                {price(product.compareAtMinor, product.currency as 'GBP')}
              </span>
            )}
          </p>
          {product.saleTitle && (
            <p className="text-[10px] uppercase tracking-widest text-sale">
              {product.saleTitle}
            </p>
          )}
        </div>
      </Link>
    </article>
  );
}

export function ProductGrid({
  products,
  eagerCount = 4,
}: {
  products: ProductCardDto[];
  eagerCount?: number;
}) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-10 lg:grid-cols-3 xl:grid-cols-4">
      {products.map((product, index) => (
        <ProductCard key={product.id} product={product} eager={index < eagerCount} />
      ))}
    </div>
  );
}
